import { Visualize3dObject } from '../../models/visualize3d-object';
import {
  OnObject3dInit,
  OnObject3dPointerEnter,
  OnObject3dPointerExit,
  OnObject3dPointerDown,
  OnObject3DSelect,
  OnObject3dDeselect,
  OnObject3dPointerUp,
} from '../../view-interactions/object-3d-events';
import { InteractiveComponent } from '../interactive-component';
import { restoreOriginalMaterial, setMaterial } from '../../utility/viewer/material-utility';
import { Injectable } from '@angular/core';
import { createThreeJsMaterial } from '../../models/three-js-material';
import { Material, Object3D } from 'three';
import type { Visualize3dUserData } from '../../models/visualize3d-user-data';

interface SelectedObject {
  object3d: Object3D;
  v3dObject: Visualize3dObject;
}

@Injectable({
  providedIn: 'root',
})
export class InteractiveComponentHandler
  implements
    OnObject3dInit<InteractiveComponent>,
    OnObject3dPointerEnter<InteractiveComponent>,
    OnObject3dPointerExit<InteractiveComponent>,
    OnObject3dPointerDown<InteractiveComponent>,
    OnObject3dPointerUp<InteractiveComponent>,
    OnObject3DSelect<InteractiveComponent>,
    OnObject3dDeselect<InteractiveComponent>
{
  private selectedObject3d: SelectedObject | undefined;

  initialize(object3d: Object3D, v3dObject: Visualize3dObject, component: InteractiveComponent): void {
    if (component.normal) {
      const material = createThreeJsMaterial(component.normal);
      this.setMaterial(object3d, material, component.normal.preserveColor);
    }
  }

  onPointerExit(object3d: Object3D, v3dObject: Visualize3dObject, component: InteractiveComponent): void {
    if (this.isSelected(v3dObject) && component.selected) {
      const material = createThreeJsMaterial(component.selected);
      this.setMaterial(object3d, material, component.selected.preserveColor);
    } else {
      this.restoreMaterial(object3d, component);
    }
  }

  onPointerEnter(object3d: Object3D, v3dObject: Visualize3dObject, component: InteractiveComponent): void {
    if (component.hover) {
      const material = createThreeJsMaterial(component.hover);
      this.setMaterial(object3d, material, component.hover.preserveColor);
    }
  }

  onPointerDown(object3d: Object3D, v3dObject: Visualize3dObject, component: InteractiveComponent): void {
    if (component.press) {
      const material = createThreeJsMaterial(component.press);
      this.setMaterial(object3d, material, component.press.preserveColor);
    }
  }

  onPointerUp(object3d: Object3D, v3dObject: Visualize3dObject, component: InteractiveComponent): void {
    if (this.isSelected(v3dObject) && component.selected) {
      const material = createThreeJsMaterial(component.selected);
      this.setMaterial(object3d, material, component.selected.preserveColor);
    }
  }

  onSelect(object3d: Object3D, v3dObject: Visualize3dObject, component: InteractiveComponent): void {
    if (!component.isSelectable) {
      return;
    }

    this.selectedObject3d = { object3d, v3dObject };
    if (component.selected) {
      const material = createThreeJsMaterial(component.selected);
      this.setMaterial(object3d, material, component.selected.preserveColor);
    }
  }

  onDeselect(object3d: Object3D, v3dObject: Visualize3dObject, component: InteractiveComponent): void {
    if (!this.selectedObject3d) {
      return;
    }

    this.restoreMaterial(this.selectedObject3d.object3d, component);
    this.selectedObject3d = undefined;
  }

  private isSelected(v3dObject: Visualize3dObject): boolean {
    return this.selectedObject3d?.v3dObject === v3dObject;
  }

  private restoreMaterial(object3d: Object3D, component: InteractiveComponent): void {
    for (const object of (object3d.userData as Visualize3dUserData).objectsWithMaterials) {
      if (component.normal) {
        const material = createThreeJsMaterial(component.normal);
        setMaterial(object, material, component.normal.preserveColor);
      } else {
        restoreOriginalMaterial(object);
      }
    }
  }

  private setMaterial(object3d: Object3D, material: Material, preserveColor: boolean): void {
    for (const object of (object3d.userData as Visualize3dUserData).objectsWithMaterials) {
      setMaterial(object, material, preserveColor);
    }
  }
}
